import { BenchmarkData, KPIData } from '../components/KPICard/KPICard';
import kpiDescriptions from '../data/kpiDescriptions.json';

// KPI calculation formulas and descriptions
export const kpiCalculations = {
  salesGrowth: {
    formula: '(Current Period Revenue - Previous Period Revenue) / Previous Period Revenue',
    description: 'Measures the rate of revenue growth compared to the previous period',
  },
  ebitdaMargin: {
    formula: 'EBITDA / Total Revenue',
    description: 'Earnings before interest, taxes, depreciation, and amortization as a percentage of revenue',
  },
  netRevenueRetention: {
    formula: '(Starting MRR + Expansion - Churned MRR) / Starting MRR',
    description: 'Measures revenue retention and expansion from existing customers',
  },
  customerAcquisitionCost: {
    formula: 'Total Sales & Marketing Costs / Number of New Customers Acquired',
    description: 'Average cost to acquire a new customer',
  },
  customerLifetimeValue: {
    formula: '(Average Revenue per Customer / Churn Rate) - Customer Acquisition Cost',
    description: 'Predicted net profit from entire future relationship with customer',
  },
  monthlyChurnRate: {
    formula: 'Customers Lost in Month / Total Customers at Start of Month',
    description: 'Percentage of customers who stop using the service each month',
  },
  employeeProductivity: {
    formula: 'Total Revenue / Number of Full-Time Employees',
    description: 'Revenue generated per employee, indicating organizational efficiency',
  },
  burnRate: {
    formula: 'Monthly Operating Expenses - Monthly Revenue',
    description: 'Rate at which company spends money (negative = profitable)',
  },
  techDebtRatio: {
    formula: 'Technical Debt Remediation Cost / Development Effort',
    description: 'Estimated effort to fix technical debt relative to total development effort',
  },
  codeQualityScore: {
    formula: 'Weighted average of code coverage, complexity, and maintainability metrics',
    description: 'Composite score indicating overall code health and maintainability',
  },
  marketShare: {
    formula: 'Company Revenue in Market / Total Addressable Market Revenue',
    description: 'Percentage of total market revenue captured by the company',
  },
  competitivePosition: {
    formula: 'Weighted score based on market share, growth rate, and feature completeness',
    description: 'Relative position compared to direct competitors',
  },
};

/**
 * Get benchmark data for a specific KPI
 * Returns real database benchmarks if available, otherwise returns undefined to show "n/a"
 * Uses benchmarkKey from kpiDescriptions.json to dynamically map KPIs to benchmark data
 */
export const getBenchmarkData = (
  kpiId: string,
  realBenchmarks?: Record<string, BenchmarkData>
): BenchmarkData | undefined => {
  const kpiDescription = kpiDescriptions[kpiId as keyof typeof kpiDescriptions];
  if (!kpiDescription) return undefined;
  if (kpiDescription.benchmarkKey === null) return undefined;

  const benchmarkKey = kpiDescription.benchmarkKey || kpiId;

  if (realBenchmarks && realBenchmarks[benchmarkKey]) {
    return realBenchmarks[benchmarkKey];
  }

  return undefined;
};

// Function to create KPI data with proper types and benchmarks
export const createKPIData = (
  id: string,
  title: string,
  value: number | string,
  category: 'financial' | 'operational' | 'market' | 'risk' | 'business_model',
  options: Partial<KPIData> = {},
  realBenchmarks?: Record<string, BenchmarkData>
): KPIData => {
  const calculation = kpiCalculations[id as keyof typeof kpiCalculations];
  const kpiDescription = kpiDescriptions[id as keyof typeof kpiDescriptions];

  const benchmark = getBenchmarkData(id, realBenchmarks);

  // Use category from kpiDescriptions.json if available, otherwise use the passed category
  const finalCategory =
    (kpiDescription?.category as 'financial' | 'operational' | 'market' | 'risk' | 'business_model') || category;

  return {
    id,
    title,
    value,
    category: finalCategory,
    calculation: calculation?.formula,
    description: calculation?.description,
    isGoodHigh: kpiDescription?.isGoodHigh ?? true, // Get from JSON, default to true
    visualType: 'simple', // default - can be overridden
    ...options, // Apply options first
    benchmark, // Then apply dynamic benchmark (this will override any hardcoded benchmark in options)
  };
};

// KPI IDs that are NOT derived from fiscal year financial data
const NON_FINANCIAL_KPI_IDS = new Set([
  'callabilityScore',
  'buyAndBuildScore',
  'onlineVisibility',
  'pricePerGoogleKeywordClick',
]);

// Function to transform existing precalculated KPIs to new format
export const transformPrecalculatedKPIs = (
  precalculatedData: any,
  realBenchmarks?: Record<string, BenchmarkData>,
  lastFiscalYear?: number | null
): KPIData[] => {
  if (!precalculatedData || precalculatedData.length === 0) return [];

  const data = precalculatedData[0]; // assuming single row
  const kpis: KPIData[] = [];

  // Financial KPIs
  if (data.salesGrowth !== null && data.salesGrowth !== undefined) {
    kpis.push(
      createKPIData(
        'salesGrowth',
        'Revenue Growth',
        data.salesGrowth,
        'financial',
        {
          formatType: 'percentage',
          visualType: 'progress',
          trend: data.salesGrowth > 0.15 ? 'up' : data.salesGrowth < 0.05 ? 'down' : 'flat',
          trendValue: 12.5, // placeholder trend value - would come from API
        },
        realBenchmarks
      )
    );
  }

  // EBIT is now shown in the company overview section

  if (data.recurringRevenueShare !== null && data.recurringRevenueShare !== undefined) {
    kpis.push(
      createKPIData(
        'recurringRevenueShare',
        'Recurring Revenue Share',
        data.recurringRevenueShare,
        'business_model',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  if (data.softwareShare !== null && data.softwareShare !== undefined) {
    kpis.push(
      createKPIData(
        'softwareShare',
        'Software Share',
        data.softwareShare,
        'business_model',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  if (data.cogsRatio !== null && data.cogsRatio !== undefined) {
    kpis.push(
      createKPIData(
        'cogsRatio',
        'COGS Ratio',
        data.cogsRatio,
        'business_model',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: false, // lower is better for costs
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // Additional Financial KPIs
  if (data.ebitMargin !== null && data.ebitMargin !== undefined) {
    kpis.push(
      createKPIData(
        'ebitMargin',
        'EBIT Margin',
        data.ebitMargin,
        'financial',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // FTE Net Contribution Margin
  if (data.fteNetContributionMargin !== null && data.fteNetContributionMargin !== undefined) {
    kpis.push(
      createKPIData(
        'fteNetContributionMargin',
        'FTE Net Contribution Margin',
        data.fteNetContributionMargin,
        'operational',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // Gross Retention Rate
  if (data.grossRetentionRate !== null && data.grossRetentionRate !== undefined) {
    kpis.push(
      createKPIData(
        'grossRetentionRate',
        'Gross Retention Rate',
        data.grossRetentionRate,
        'financial',
        {
          formatType: 'percentage',
          visualType: 'gauge',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // Downgrade ARR Rate
  if (data.downgradeArrRate !== null && data.downgradeArrRate !== undefined) {
    kpis.push(
      createKPIData(
        'downgradeArrRate',
        'Downgrade ARR Rate',
        data.downgradeArrRate,
        'risk',
        {
          formatType: 'percentage',
          visualType: 'gauge',
          isGoodHigh: false, // lower is better
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // Gross New Customer Logo Growth Rate
  if (data.grossNewCustomerLogoGrowthRate !== null && data.grossNewCustomerLogoGrowthRate !== undefined) {
    kpis.push(
      createKPIData(
        'grossNewCustomerLogoGrowthRate',
        'New Customer Logo Growth',
        data.grossNewCustomerLogoGrowthRate,
        'financial',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  if (data.ebitdaMargin !== null && data.ebitdaMargin !== undefined) {
    kpis.push(
      createKPIData(
        'ebitdaMargin',
        'EBITDA Margin',
        data.ebitdaMargin,
        'financial',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  if (data.opexRatio !== null && data.opexRatio !== undefined) {
    kpis.push(
      createKPIData(
        'opexRatio',
        'OPEX Ratio',
        data.opexRatio,
        'financial',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: false, // lower is better
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  if (data.grossNewCustomerSalesGrowthRate !== null && data.grossNewCustomerSalesGrowthRate !== undefined) {
    kpis.push(
      createKPIData(
        'grossNewCustomerSalesGrowthRate',
        'Gross New Customer Growth',
        data.grossNewCustomerSalesGrowthRate,
        'financial',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  if (data.grossArrChurnRate !== null && data.grossArrChurnRate !== undefined) {
    kpis.push(
      createKPIData(
        'grossArrChurnRate',
        'Gross ARR Churn Rate',
        data.grossArrChurnRate,
        'financial',
        {
          formatType: 'percentage',
          visualType: 'gauge',
          isGoodHigh: false, // lower churn is better
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  if (data.grossLogoChurnRate !== null && data.grossLogoChurnRate !== undefined) {
    kpis.push(
      createKPIData(
        'grossLogoChurnRate',
        'Gross Logo Churn Rate',
        data.grossLogoChurnRate,
        'financial',
        {
          formatType: 'percentage',
          visualType: 'gauge',
          isGoodHigh: false, // lower churn is better
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // Additional Business Model KPIs
  if (data.top3CustomerShare !== null && data.top3CustomerShare !== undefined) {
    kpis.push(
      createKPIData(
        'top3CustomerShare',
        'Top 3 Customer Share',
        data.top3CustomerShare,
        'business_model',
        {
          formatType: 'percentage',
          visualType: 'gauge',
          isGoodHigh: false, // lower concentration is better for risk
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // Operational KPIs
  if (data.employeeConcentrationRatio !== null && data.employeeConcentrationRatio !== undefined) {
    kpis.push(
      createKPIData(
        'employeeConcentrationRatio',
        'Employee Concentration',
        data.employeeConcentrationRatio,
        'market',
        {
          formatType: 'percentage',
          visualType: 'gauge',
          isGoodHigh: false, // lower concentration is better for risk
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  if (data.teamQuality !== null && data.teamQuality !== undefined) {
    kpis.push(
      createKPIData(
        'teamQuality',
        'Team Quality Score',
        data.teamQuality,
        'operational',
        {
          formatType: 'percentage',
          visualType: 'gauge',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  if (data.cLevelQuality !== null && data.cLevelQuality !== undefined) {
    kpis.push(
      createKPIData(
        'cLevelQuality',
        'C-Level Quality',
        data.cLevelQuality,
        'operational',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // Growth KPIs
  if (data.softwareCategoryGrowth !== null && data.softwareCategoryGrowth !== undefined) {
    kpis.push(
      createKPIData(
        'softwareCategoryGrowth',
        'Category Growth Rate',
        data.softwareCategoryGrowth,
        'market',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  if (data.customerIndustryGrowth !== null && data.customerIndustryGrowth !== undefined) {
    kpis.push(
      createKPIData(
        'customerIndustryGrowth',
        'Customer Industry Growth',
        data.customerIndustryGrowth,
        'market',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  if (data.netNewCustomerSalesGrowth !== null && data.netNewCustomerSalesGrowth !== undefined) {
    kpis.push(
      createKPIData(
        'netNewCustomerSalesGrowth',
        'New Customer Growth',
        data.netNewCustomerSalesGrowth,
        'financial',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // Technology KPIs removed from display

  // Risk/Assessment KPIs
  if (data.callabilityScore !== null && data.callabilityScore !== undefined) {
    kpis.push(
      createKPIData(
        'callabilityScore',
        'Callability Score',
        data.callabilityScore,
        'risk',
        {
          formatType: 'percentage',
          visualType: 'gauge',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  if (data.buyAndBuildScore !== null && data.buyAndBuildScore !== undefined) {
    kpis.push(
      createKPIData(
        'buyAndBuildScore',
        'Buy & Build Score',
        data.buyAndBuildScore,
        'risk',
        {
          formatType: 'number',
          visualType: 'gauge',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // Online/Marketing KPIs
  if (data.onlineVisibility !== null && data.onlineVisibility !== undefined) {
    kpis.push(
      createKPIData(
        'onlineVisibility',
        'Online Visibility',
        data.onlineVisibility,
        'market',
        {
          formatType: 'number',
          visualType: 'progress',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  if (data.pricePerGoogleKeywordClick !== null && data.pricePerGoogleKeywordClick !== undefined) {
    kpis.push(
      createKPIData(
        'pricePerGoogleKeywordClick',
        'Avg. CPC',
        data.pricePerGoogleKeywordClick,
        'operational',
        {
          formatType: 'currency',
          visualType: 'simple',
          isGoodHigh: false, // lower cost is better
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // Add fiscal year labels to financial KPIs
  if (lastFiscalYear) {
    const label = `FY ${lastFiscalYear}`;
    kpis.forEach(kpi => {
      if (!NON_FINANCIAL_KPI_IDS.has(kpi.id)) {
        kpi.yearLabel = label;
      }
    });
  }

  return kpis;
};

// Additional KPIs using real backend data
export const createAdditionalKPIs = (
  data?: any,
  realBenchmarks?: Record<string, BenchmarkData>,
  lastFiscalYear?: number | null
): KPIData[] => {
  if (!data || data.length === 0) return [];

  const kpiData = data[0];
  const additionalKpis: KPIData[] = [];

  // Net Revenue Retention (using netRetentionRate from backend)
  if (kpiData.netRetentionRate !== null && kpiData.netRetentionRate !== undefined) {
    additionalKpis.push(
      createKPIData(
        'netRetentionRate',
        'Net Revenue Retention',
        kpiData.netRetentionRate,
        'financial',
        {
          formatType: 'ratio',
          visualType: 'gauge',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // Revenue per Employee
  if (kpiData.absSales !== null && kpiData.absFte !== null && kpiData.absFte > 0) {
    const revenuePerEmployee = (kpiData.absSales * 1000) / kpiData.absFte; // Convert back from thousands
    additionalKpis.push(
      createKPIData(
        'employeeProductivity',
        'Revenue per Employee',
        revenuePerEmployee,
        'operational',
        {
          formatType: 'currency',
          visualType: 'progress',
          isGoodHigh: true,
        },
        realBenchmarks
      )
    );
  }

  // Enterprise Value Estimation
  if (kpiData.enterpriseValueEstimation !== null && kpiData.enterpriseValueEstimation !== undefined) {
    additionalKpis.push(
      createKPIData('enterpriseValue', 'Enterprise Value Estimation', kpiData.enterpriseValueEstimation, 'financial', {
        formatType: 'currency',
        visualType: 'simple',
        isGoodHigh: true,
      })
    );
  }

  // Net ARR Churn Rate
  if (kpiData.netArrChurnRate !== null && kpiData.netArrChurnRate !== undefined) {
    additionalKpis.push(
      createKPIData(
        'netArrChurnRate',
        'Net ARR Churn Rate',
        kpiData.netArrChurnRate,
        'financial',
        {
          formatType: 'percentage',
          visualType: 'gauge',
          isGoodHigh: false,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // Expansion ARR Rate
  if (kpiData.expansionArrRate !== null && kpiData.expansionArrRate !== undefined) {
    additionalKpis.push(
      createKPIData(
        'expansionArrRate',
        'Expansion ARR Rate',
        kpiData.expansionArrRate,
        'financial',
        {
          formatType: 'percentage',
          visualType: 'progress',
          isGoodHigh: true,
          // Remove hardcoded benchmark - will use getBenchmarkData function
        },
        realBenchmarks
      )
    );
  }

  // Add fiscal year labels to all additional KPIs (all are financial)
  if (lastFiscalYear) {
    const label = `FY ${lastFiscalYear}`;
    additionalKpis.forEach(kpi => {
      kpi.yearLabel = label;
    });
  }

  return additionalKpis;
};
